// @/services/auth/keycloak.js
import Keycloak from 'keycloak-js';
import { cacheManager } from '@/services/app/CacheManager';
import { trackAuth } from '@/utils/tracking';
import { logger } from '@/utils/logger';
import { getKeycloakUrl, getAuthRedirectUri } from '@/services/auth/keycloak-url';

const log = logger.keycloak;

/**
 * KeycloakAuthService
 *
 * Production-ready Keycloak authentication service with simplified logout.
 * Handles authentication, token management, and user profile operations.
 * 
 * Key features:
 * - Secure token management with automatic refresh
 * - User profile caching for performance
 * - Cross-tab authentication state sharing
 * - Simplified logout that works with backend endpoint
 */
class KeycloakAuthService {
  /**
   * Initialize the KeycloakAuthService
   */
  constructor() {
    this.state = {
      initialized: false,
      authenticated: false,
      token: null,
      userProfile: null,
      tokenExpiry: null,
      initializing: false
    };

    // Initialize Keycloak instance
    this.keycloak = new Keycloak({
      url: this.getBaseUrl(),
      realm: process.env.GATSBY_KEYCLOAK_REALM || 'platform',
      clientId: process.env.GATSBY_KEYCLOAK_CLIENT_ID || 'platform'
    });

    // Set up minimal event listeners
    if (this.keycloak) {
      this.keycloak.onTokenExpired = () => {
        // CRITICAL: Check logout state BEFORE refreshing
        if (typeof sessionStorage !== 'undefined') {
          const sessionTimeout = sessionStorage.getItem('sessionTimeout') === 'true';
          const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
          const crossTabLogout = sessionStorage.getItem('crossTabLogout') === 'true';

          if (sessionTimeout || controlledLogout || crossTabLogout) {
            log.log('Token expired but logout in progress - skipping refresh');
            return;
          }
        }

        this.refreshToken();
      };

      this.keycloak.onAuthSuccess = () => this.updateTokenInfo();
      this.keycloak.onAuthError = () => {
        log.warn('Auth error');
      };
    }

    this.tokenRefreshInterval = null;
    this.authInitPromise = null;
  }

  /**
   * Get Keycloak base URL — hostname-aware so legacy *.supplier-s.app
   * users hit auth.supplier-s.io and new *.ctrl-s.io users hit auth.ctrl-s.io.
   */
  getBaseUrl() {
    return getKeycloakUrl();
  }

  /**
   * Parse JWT token into JSON object with proper UTF-8 support
   * Fixed to handle Chinese characters and base64url encoding
   */
  parseJwt(token) {
    try {
      if (!token) return null;
      const tokenParts = token.split('.');
      if (tokenParts.length < 2) return null;

      // Convert base64url to base64 by replacing URL-safe characters
      let base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');

      // Add padding if needed (base64url removes padding)
      while (base64.length % 4) {
        base64 += '=';
      }

      // Modern approach: Use TextDecoder for proper UTF-8 handling
      try {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const jsonString = new TextDecoder('utf-8').decode(bytes);
        return JSON.parse(jsonString);
      } catch (decodingError) {
        // Fallback: Use the classic escape/decodeURIComponent method
        const jsonString = decodeURIComponent(escape(atob(base64)));
        return JSON.parse(jsonString);
      }
    } catch (error) {
      log.warn('Error parsing JWT', error);
      return null;
    }
  }

  /**
   * Validate if a token is still valid
   * Checks both format and expiration
   */
  isTokenValid(token) {
    if (!token || typeof token !== 'string') return false;

    try {
      const parsed = this.parseJwt(token);
      if (!parsed || !parsed.exp) return false;

      // Add 30 second buffer for clock skew
      const expirationTime = (parsed.exp * 1000) - 30000;
      return Date.now() < expirationTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get cached user profile with token validation
   */
  getCachedUserProfile() {
    try {
      // Use CacheManager to get cached auth state
      const cachedAuth = cacheManager.getAuthState();

      if (cachedAuth && cachedAuth.userProfile && cachedAuth.token) {
        // Validate token before trusting cached profile
        if (this.isTokenValid(cachedAuth.token)) {
          return cachedAuth.userProfile;
        } else {
          log.log('Cached token is expired, clearing cached profile');
          // Clear expired cache
          this.clearCachedProfile();
          return null;
        }
      }

      return null;
    } catch (e) {
      log.warn('Error getting cached user profile:', e);
      return null;
    }
  }

  /**
   * Clear cached profile data
   */
  clearCachedProfile() {
    try {
      cacheManager.clearAuthState();
    } catch (e) {
      // Ignore errors
    }
  }

  /**
   * Get authentication data for cross-tab access with validation
   */
  getCrossTabAuth() {
    try {
      const crossTabAuth = cacheManager.getCrossTabAuth();

      if (!crossTabAuth) return null;

      // Validate token before trusting cross-tab auth
      if (crossTabAuth.token && !this.isTokenValid(crossTabAuth.token)) {
        log.log('Cross-tab token is expired, clearing cross-tab auth');
        cacheManager.clearCrossTabData();
        return null;
      }

      return crossTabAuth;
    } catch (e) {
      log.warn('Error reading cross-tab auth:', e);
      return null;
    }
  }

  /**
   * Initialize authentication with better error handling
   * FIXED: Enhanced logout page detection and state handling
   */
  async initialize() {

    // Check if we're in session timeout state
    if (typeof sessionStorage !== 'undefined') {
      const sessionTimeout = sessionStorage.getItem('sessionTimeout') === 'true';
      if (sessionTimeout) {
        log.log('Session timeout detected, skipping initialization');
        this.state.initialized = true;
        this.state.authenticated = false;
        return false;
      }
    }

    // Never initialize on logout page or during logout process
    if (typeof window !== 'undefined') {
      const isLogoutPage = window.location.pathname === '/logout' ||
        window.location.pathname === '/logout/';

      if (isLogoutPage) {
        log.log('Skipping initialization on logout page');
        this.state.initialized = true;
        this.state.authenticated = false;
        return false;
      }

      // Check for logout state flags
      if (typeof sessionStorage !== 'undefined') {
        const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
        const skipAuth = sessionStorage.getItem('skipAuthProcessing') === 'true';
        const onLogoutPage = sessionStorage.getItem('onLogoutPage') === 'true';

        if (controlledLogout || skipAuth || onLogoutPage) {
          log.log('Skipping initialization due to logout state');
          this.state.initialized = true;
          this.state.authenticated = false;
          return false;
        }
      }

      // CRITICAL: Clear auth codes from URL if we're not supposed to process them
      const params = new URLSearchParams(window.location.search);
      const hasAuthCode = params.has('code') || params.has('state') ||
        params.has('session_state') || params.has('iss');

      if (hasAuthCode) {
        // Check if we should process auth codes
        const shouldSkipAuth = typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('skipAuthProcessing') === 'true';

        if (shouldSkipAuth) {
          log.log('Found auth codes but skipping due to logout state');

          // Clear the auth codes from URL
          try {
            const newUrl = new URL(window.location.href);
            ['code', 'state', 'session_state', 'iss'].forEach(param => {
              newUrl.searchParams.delete(param);
            });
            window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
          } catch (e) {
            log.warn('Error clearing auth codes:', e);
          }

          this.state.initialized = true;
          this.state.authenticated = false;
          return false;
        }
      }
    }

    // Return existing promise if initialization is in progress
    if (this.authInitPromise) {
      return this.authInitPromise;
    }

    // Prevent multiple initialization attempts
    if (this.state.initializing) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.state.initializing) {
            clearInterval(checkInterval);
            resolve(this.state.authenticated);
          }
        }, 100);
      });
    }

    // Create new initialization promise
    this.authInitPromise = this._performInitialization();

    try {
      const result = await this.authInitPromise;
      return result;
    } catch (error) {
      log.error('Authentication initialization error:', error);
      this.authInitPromise = null;
      return false;
    }
  }

  /**
   * Perform initialization with better token validation
   * ENHANCED: Better logout state handling
   */
  async _performInitialization() {
    this.state.initializing = true;

    try {
      // Double-check logout state before proceeding
      if (typeof window !== 'undefined') {
        const isLogoutPage = window.location.pathname === '/logout' ||
          window.location.pathname === '/logout/';

        if (isLogoutPage) {
          this.state.initializing = false;
          this.state.initialized = true;
          this.state.authenticated = false;
          return false;
        }

        if (typeof sessionStorage !== 'undefined') {
          const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
          if (controlledLogout) {
            this.state.initializing = false;
            this.state.initialized = true;
            this.state.authenticated = false;
            return false;
          }
        }
      }

      // Check for cross-tab auth with proper validation
      const crossTabAuth = this.getCrossTabAuth();

      // Use cross-tab auth only if token is valid AND we're not in logout state
      if (crossTabAuth?.authenticated && crossTabAuth.projectId &&
        this.isTokenValid(crossTabAuth.token)) {

        // Double-check we're not in logout state
        if (typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('controlledLogout') === 'true') {
          log.log('Found cross-tab auth but in logout state, ignoring');
        } else {
          log.log('Found valid cross-tab auth data');

          this.state.initialized = true;
          this.state.authenticated = true;
          this.state.token = crossTabAuth.token;
          this.state.userProfile = crossTabAuth.user || { projectId: crossTabAuth.projectId };

          // Parse token expiry
          const tokenPayload = this.parseJwt(crossTabAuth.token);
          if (tokenPayload?.exp) {
            this.state.tokenExpiry = tokenPayload.exp * 1000;
          }

          // CRITICAL: Set tokens on keycloak instance for logout to work
          if (this.keycloak) {
            this.keycloak.token = crossTabAuth.token;
            this.keycloak.authenticated = true;
            // Also restore refresh/id tokens if available in crossTabAuth
            if (crossTabAuth.refreshToken) this.keycloak.refreshToken = crossTabAuth.refreshToken;
            if (crossTabAuth.idToken) this.keycloak.idToken = crossTabAuth.idToken;
          }

          // Refresh in background, then keep refreshing (ticket #1044)
          setTimeout(() => this.refreshToken().finally(() => this.setupTokenRefresh()), 100);

          this.state.initializing = false;
          return true;
        }
      }

      // Check for page reload with better validation
      if (typeof window !== 'undefined') {
        const isPageReload = window.performance?.navigation?.type === 1;
        if (isPageReload) {
          // Make sure we're not in logout state
          if (typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem('controlledLogout') === 'true') {
            this.state.initializing = false;
            this.state.initialized = true;
            this.state.authenticated = false;
            return false;
          }

          // IMPORTANT: Get cached auth FIRST before getCachedUserProfile()
          // because getCachedUserProfile() may clear cache if access token expired
          const cachedAuth = cacheManager.getAuthState();

          // Always set tokens on keycloak instance if we have them (for logout to work)
          // Do this BEFORE getCachedUserProfile which may clear them
          if (cachedAuth && this.keycloak) {
            if (cachedAuth.refreshToken) this.keycloak.refreshToken = cachedAuth.refreshToken;
            if (cachedAuth.idToken) this.keycloak.idToken = cachedAuth.idToken;
          }

          const cachedProfile = this.getCachedUserProfile();

          if (cachedProfile && cachedAuth && cachedAuth.token) {
            this.state.initialized = true;
            this.state.authenticated = true;
            this.state.userProfile = cachedProfile;
            this.state.token = cachedAuth.token;
            this.state.tokenExpiry = cachedAuth.tokenExpiry;

            // Set access token and authenticated flag on keycloak instance
            if (this.keycloak) {
              this.keycloak.token = cachedAuth.token;
              this.keycloak.authenticated = true;
            }

            // Refresh in background, then keep refreshing. Without the interval
            // a restored session's token expired after one lifespan and saves
            // failed with 401 (ticket #1044). It is armed after the first
            // refresh so its period follows a full-lifespan token, not the
            // nearly expired cached one.
            setTimeout(() => this.refreshToken().finally(() => this.setupTokenRefresh()), 0);

            this.state.initializing = false;
            return true;
          }
        }
      }

      // Skip auth for invitation tokens
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);

        if (params.get('token') || params.get('invitation') || params.get('invitationToken')) {
          this.state.initializing = false;
          return false;
        }

        // Check for code parameter from Keycloak
        const code = params.get('code');
        if (code) {
          // ADD THIS CHECK - Don't process code on logout page
          if (window.location.pathname === '/logout' || window.location.pathname === '/logout/') {
            log.log('Ignoring auth code on logout page');
            this.state.initializing = false;
            this.state.initialized = true;
            this.state.authenticated = false;
            return false;
          }

          // Make sure we should process this code
          if (typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem('skipAuthProcessing') === 'true') {
            log.log('Found auth code but skipping due to logout state');
            this.state.initializing = false;
            return false;
          }


          try {
            const success = await this.exchangeCode(code);
            if (success) {
              this.state.initialized = true;
              this.state.authenticated = true;
              await this.loadUserProfile();

              // CRITICAL: Detect projectId change (different user logging in)
              // If the new user has a different projectId than cached, clear all app state
              if (this.state.userProfile?.projectId) {
                const newProjectId = this.state.userProfile.projectId;
                const cachedProjectId = cacheManager.getProjectId();

                if (cachedProjectId && cachedProjectId !== newProjectId) {
                  log.log(`ProjectId changed from ${cachedProjectId} to ${newProjectId}, clearing cached app state`);

                  // Clear app state but NOT auth state (we just authenticated)
                  cacheManager.clearAppState();

                  // Also clear the app store if it exists
                  if (window.appStore?.clearState) {
                    try {
                      await window.appStore.clearState();
                    } catch (e) {
                      log.warn('Error clearing app store:', e);
                    }
                  }
                }
              }

              this.setupTokenRefresh();

              if (this.state.userProfile) {
                this.cacheUserProfile(this.state.userProfile);
                this.storeCrossTabAuth();
              }

              // TRACK USER LOGIN
              trackAuth.login('keycloak');

              // Remove the Keycloak callback params from the URL
              if (window.history && window.history.replaceState) {
                window.history.replaceState({}, document.title, `${getAuthRedirectUri()}${window.location.hash}`);
              }

              this.state.initializing = false;
              return true;
            }
          } catch (error) {
            log.error('Failed to exchange code:', error);
          }
        }
      }

      // Regular Keycloak initialization
      const initOptions = {
        onLoad: 'check-sso',
        checkLoginIframe: false,
        flow: 'standard',
        redirectUri: getAuthRedirectUri()
      };

      try {
        // Check if already initialized
        if (this.keycloak._initialized) {
          log.log('Keycloak already initialized, using existing state');
          this.state.initialized = true;
          this.state.authenticated = this.keycloak.authenticated || false;

          if (this.state.authenticated) {
            this.state.token = this.keycloak.token;
            await this.loadUserProfile();

            if (this.state.userProfile) {
              this.cacheUserProfile(this.state.userProfile);
              this.storeCrossTabAuth();
            }
          }

          this.state.initializing = false;
          return this.state.authenticated;
        }

        // Initialize with timeout protection
        let timeoutId;

        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('Keycloak initialization timed out'));
          }, 15000);
        });

        try {
          const initPromise = this.keycloak.init(initOptions);
          const authenticated = await Promise.race([initPromise, timeoutPromise]);

          clearTimeout(timeoutId);

          this.state.initialized = true;
          this.state.authenticated = authenticated;

          if (authenticated) {
            this.state.token = this.keycloak.token;
            await this.loadUserProfile();

            if (this.state.userProfile) {
              this.cacheUserProfile(this.state.userProfile);
              this.storeCrossTabAuth();
            }

            this.setupTokenRefresh();

            // TRACK USER LOGIN
            trackAuth.login('keycloak');
          }

          this.state.initializing = false;
          return authenticated;
        } catch (timeoutError) {
          clearTimeout(timeoutId);

          log.warn('Keycloak initialization timed out');

          this.state.initialized = true;
          this.state.authenticated = false;
          this.state.initializing = false;
          return false;
        }
      } catch (error) {
        // Handle "already initialized" error
        if (error.message && error.message.includes('can only be initialized once')) {
          this.state.initialized = true;
          this.state.authenticated = this.keycloak.authenticated || false;

          if (this.state.authenticated) {
            this.state.token = this.keycloak.token;

            try {
              await this.loadUserProfile();
              if (this.state.userProfile) {
                this.cacheUserProfile(this.state.userProfile);
                this.storeCrossTabAuth();
              }
            } catch (profileError) {
              log.warn('Error loading profile after "already initialized":', profileError);
            }
          }

          this.state.initializing = false;
          return this.state.authenticated;
        }

        log.error('Keycloak initialization error:', error);
        this.state.initialized = true;
        this.state.authenticated = false;
        this.state.initializing = false;
        return false;
      }
    } catch (error) {
      log.error('Initialization error:', error);
      this.state.initialized = true;
      this.state.authenticated = false;
      this.state.initializing = false;
      return false;
    }
  }

  /**
   * Refresh token with better error handling and endpoint validation
   */
  async refreshToken() {
    if (!this.keycloak?.authenticated || !this.keycloak.refreshToken) {
      return false;
    }

    // Don't refresh during logout
    if (typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('controlledLogout') === 'true') {
      log.log('Skipping token refresh during logout');
      return false;
    }

    try {
      // Check if Keycloak endpoints are properly initialized
      if (!this.keycloak.endpoints) {
        log.info('Keycloak endpoints not initialized, attempting manual refresh');

        // Try manual token refresh via API
        return await this.manualTokenRefresh();
      }

      const refreshed = await this.keycloak.updateToken(10);
      if (refreshed) {
        this.updateTokenInfo();
        this.storeCrossTabAuth();

        // Dispatch auth state change event for UI components
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('authStateChange', {
            detail: { type: 'tokenRefresh', authenticated: true }
          }));
        }

        return true;
      }
      return false;
    } catch (error) {
      log.warn('Token refresh failed:', error?.message || 'Unknown error');

      // DON'T clear auth state on refresh failure - this is too aggressive
      // The user may still have a valid session, just a temporary network issue
      // Instead, let the next auth check or page load handle re-authentication
      // Only log the error and return false

      return false;
    }
  }

  /**
   * Manual token refresh when Keycloak endpoints aren't available.
   * Goes through the backend so the Keycloak client secret stays server-side.
   */
  async manualTokenRefresh() {
    try {
      const tokenUrl = `${process.env.GATSBY_STRAPI_URL}/api/auth/refresh-token`;

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: this.keycloak.refreshToken, issuer: this.getBaseUrl() })
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const tokenData = await response.json();

      // Update Keycloak instance
      this.keycloak.token = tokenData.access_token;
      this.keycloak.refreshToken = tokenData.refresh_token;
      if (tokenData.id_token) {
        this.keycloak.idToken = tokenData.id_token;
      }

      this.updateTokenInfo();
      this.storeCrossTabAuth();

      // Re-arm the periodic refresh. The cache-restore boot paths (cross-tab
      // and page-reload) adopt tokens without ever calling setupTokenRefresh,
      // so a reloaded tab used to hold a 5-minute token that nothing renewed:
      // the session silently died mid-page ~5 minutes after the last refresh.
      // Every successful manual refresh runs through here, so arming here
      // covers both restore paths and keeps the interval in sync with the
      // fresh expiry. setupTokenRefresh clears any existing interval first.
      this.setupTokenRefresh();

      // Dispatch auth state change event for UI components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authStateChange', {
          detail: { type: 'manualTokenRefresh', authenticated: true }
        }));
      }

      log.log('Manual token refresh successful');

      return true;
    } catch (error) {
      log.error('Manual token refresh failed:', error);
      return false;
    }
  }

  /**
   * Store authentication data for cross-tab access
   */
  storeCrossTabAuth() {
    if (!this.state.authenticated || !this.state.userProfile || !this.state.token) return;

    // Only store if token is valid
    if (!this.isTokenValid(this.state.token)) {
      log.log('Not storing cross-tab auth: token is invalid');
      return;
    }

    try {
      cacheManager.saveCrossTabAuth({
        projectId: this.state.userProfile.projectId,
        userProfile: this.state.userProfile,
        token: this.state.token,
        refreshToken: this.keycloak?.refreshToken,
        idToken: this.keycloak?.idToken
      });
    } catch (e) {
      log.warn('Error storing cross-tab auth:', e);
    }
  }

  /**
   * Cache user profile for persistence during page reloads
   */
  cacheUserProfile(profile) {
    if (!profile) return;

    try {
      cacheManager.saveAuthState({
        authenticated: true,
        userProfile: profile,
        token: this.state.token,
        refreshToken: this.keycloak.refreshToken,
        idToken: this.keycloak.idToken,
        tokenExpiry: this.state.tokenExpiry || (Date.now() + 300000),
        projectId: profile.projectId
      });

      // Dispatch auth state change event for UI components (e.g., sidebar avatar)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authStateChange', {
          detail: { type: 'profileCached', authenticated: true, user: profile }
        }));
      }
    } catch (e) {
      log.warn('Error caching user profile:', e);
    }
  }

  /**
   * Exchange authorization code for tokens.
   * Goes through the backend so the Keycloak client secret stays server-side.
   */
  async exchangeCode(code) {
    try {
      log.log('Exchanging authorization code for tokens');

      const redirectUri = getAuthRedirectUri();

      const tokenUrl = `${process.env.GATSBY_STRAPI_URL}/api/auth/exchange-token`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(tokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          // issuer: the backend must mint tokens at the same auth host this
          // domain's SSO session lives on (hostname-aware Keycloak)
          body: JSON.stringify({ code, redirectUri, issuer: this.getBaseUrl() }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorDetail = '';
          try {
            const errorData = await response.json();
            errorDetail = JSON.stringify(errorData);
            log.error('Token exchange error details:', errorData);
          } catch (e) {
            errorDetail = `Status ${response.status}`;
          }

          throw new Error(`Token exchange failed: ${errorDetail}`);
        }

        const tokenData = await response.json();

        // Update Keycloak instance
        this.keycloak.token = tokenData.access_token;
        this.keycloak.refreshToken = tokenData.refresh_token;
        this.keycloak.idToken = tokenData.id_token;
        this.keycloak.authenticated = true;

        try {
          this.keycloak.tokenParsed = this.parseJwt(tokenData.access_token);
        } catch (e) {
          log.warn('Could not parse token:', e);
        }

        this.updateTokenInfo();
        return true;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
    } catch (error) {
      log.error('Failed to exchange code:', error);
      return false;
    }
  }

  /**
   * Update token information from Keycloak instance
   */
  updateTokenInfo() {
    if (!this.keycloak || !this.keycloak.token) return;

    this.state.token = this.keycloak.token;

    try {
      const tokenPayload = this.parseJwt(this.keycloak.token);
      if (tokenPayload && tokenPayload.exp) {
        this.state.tokenExpiry = tokenPayload.exp * 1000;
      }
    } catch (error) {
      log.error('Error decoding token expiry:', error);
    }
  }

  /**
   * Setup token refresh interval
   */
  setupTokenRefresh() {
    if (!this.keycloak?.authenticated) return;

    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    const refreshTime = this.getRefreshTiming();
    this.tokenRefreshInterval = setInterval(() => {
      this.refreshToken();
    }, refreshTime);
  }

  /**
   * Get refresh timing based on token expiry
   */
  getRefreshTiming() {
    if (!this.state.tokenExpiry) {
      return 4 * 60 * 1000; // Default: 4 minutes
    }

    const now = Date.now();
    const expiresIn = this.state.tokenExpiry - now;

    if (expiresIn < 30 * 1000) {
      return 1000; // 1 second minimum
    }

    return Math.min(Math.floor(expiresIn * 0.8), 5 * 60 * 1000);
  }

  /**
   * Load user profile from Keycloak
   */
  async loadUserProfile() {
    if (!this.keycloak?.authenticated) {
      return null;
    }

    // Return existing profile if available and valid
    if (this.state.userProfile?.projectId) {
      return this.state.userProfile;
    }

    try {
      const profileUrl = `${this.getBaseUrl()}/realms/${process.env.GATSBY_KEYCLOAK_REALM}/protocol/openid-connect/userinfo`;

      const response = await fetch(profileUrl, {
        headers: {
          'Authorization': `Bearer ${this.keycloak.token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load profile: ${response.status}`);
      }

      const profile = await response.json();

      // Extract projectId
      let projectId = profile.ProjectId || profile.projectId;

      if (!projectId && profile.attributes) {
        projectId = profile.attributes.projectId?.[0] ||
          profile.attributes.ProjectId?.[0] ||
          profile.attributes["project-id"]?.[0] ||
          profile.attributes.project_id?.[0];
      }

      if (!projectId && this.keycloak.idTokenParsed) {
        projectId = this.keycloak.idTokenParsed.projectId ||
          this.keycloak.idTokenParsed.ProjectId;
      }

      if (!projectId) {
        throw new Error('Could not determine projectId from profile or tokens');
      }

      const userProfile = {
        id: profile.sub || '',
        email: profile.email || '',
        name: profile.name || `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
        firstName: profile.given_name || '',
        lastName: profile.family_name || '',
        projectId: projectId
      };

      this.cacheUserProfile(userProfile);
      this.state.userProfile = userProfile;
      return userProfile;
    } catch (error) {
      log.error('Error loading user profile:', error);
      return null;
    }
  }

  /**
   * Login user
   */
  async login(email = null) {

    // CRITICAL: Check for ANY logout state before attempting login
    if (typeof window !== 'undefined') {
      // Check if we're on logout page
      const isLogoutPage = window.location.pathname === '/logout' ||
        window.location.pathname === '/logout/';

      if (isLogoutPage) {
        log.log('Login blocked - on logout page');
        return false;
      }
    }

    // CRITICAL: Never redirect to login if in logout state
    if (typeof sessionStorage !== 'undefined') {
      const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
      const crossTabLogout = sessionStorage.getItem('crossTabLogout') === 'true';
      const onLogoutPage = sessionStorage.getItem('onLogoutPage') === 'true';
      const sessionTimeout = sessionStorage.getItem('sessionTimeout') === 'true';

      if (controlledLogout || crossTabLogout || onLogoutPage || sessionTimeout) {
        log.log('Login blocked - logout in progress');
        return false;
      }
    }

    try {
      const redirectUri = getAuthRedirectUri();

      const authUrl = `${this.getBaseUrl()}/realms/${process.env.GATSBY_KEYCLOAK_REALM}/protocol/openid-connect/auth`;
      const clientId = process.env.GATSBY_KEYCLOAK_CLIENT_ID;

      const loginUrl = `${authUrl}?` +
        `client_id=${encodeURIComponent(clientId)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=code` +
        `&scope=${encodeURIComponent('openid profile email')}` +
        (email ? `&login_hint=${encodeURIComponent(email)}` : '');

      window.location.href = loginUrl;
      return true;
    } catch (error) {
      log.error('Login failed:', error);
      return false;
    }
  }

  /**
   * SIMPLIFIED LOGOUT - Just clear client state
   * The actual Keycloak logout is handled by the backend
   * This prevents ANY redirect to auth.supplier-s.io
   */
  async logout() {
    log.log('Clearing client-side authentication state');

    // Clear token refresh interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    // Clear client-side state
    this.state.authenticated = false;
    this.state.token = null;
    this.state.userProfile = null;
    this.state.tokenExpiry = null;

    // Use CacheManager to clear all cache
    cacheManager.clearAll();

    // Clear Keycloak instance state
    if (this.keycloak) {
      this.keycloak.authenticated = false;
      this.keycloak.token = null;
      this.keycloak.refreshToken = null;
      this.keycloak.idToken = null;
      this.keycloak.tokenParsed = null;
      this.keycloak.refreshTokenParsed = null;
      this.keycloak.idTokenParsed = null;
      this.keycloak.realmAccess = null;
      this.keycloak.resourceAccess = null;
    }

    log.log('Client-side logout complete - all auth state cleared');

    // Don't redirect - let the caller handle navigation
    return true;
  }

  /**
   * Clear token from Keycloak instance
   */
  clearToken() {
    if (this.keycloak) {
      this.keycloak.clearToken();
    }
  }

  /**
   * Get current authentication state
   */
  getAuthState() {
    return {
      authenticated: this.state.authenticated,
      user: this.state.userProfile,
      token: this.state.token,
      tokenExpiry: this.state.tokenExpiry
    };
  }

  /**
   * Get project ID from user profile
   */
  getProjectId() {
    return this.state.userProfile?.projectId || null;
  }

  /**
   * Clear auth state for testing or force re-authentication
   */
  clearAuthState() {
    this.state.authenticated = false;
    this.state.token = null;
    this.state.userProfile = null;
    this.state.tokenExpiry = null;

    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }
}

export const keycloakAuthService = new KeycloakAuthService();